import { useEffect, useMemo, useState } from "react";
import { Info, Search } from "react-feather";
import { toast } from "react-toastify";
import { Select } from "semantic-ui-react";
import { countryDetailsById, countryNameByRegion } from "../../api/countryApi";
import {
  geoDropdownById,
  geoSearch,
  getGeoUnitById,
} from "../../api/geoHierarchyApi";
import { getDropdownData } from "../../api/keyvalue";
import { getByIdRegion, regionDropdown } from "../../api/regionApi";
import Grid from "../../components/Grid";
import ActionMenu from "../../components/input/ActionMenu";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import SearchDropdown from "../../components/input/SearchDropdown";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import GeoManagerAddComponent from "../../components/master/GeoManagerAddComponent";
import GeoUnitsUpdateComponent from "../../components/master/GeoUnitsUpdateComponent";
import { ActiveCellStyle } from "../../components/renderer/StyleRenderer";
import Tab from "../../components/Tab";
import useUserCompany from "../../hooks/useUserCompany";

function Geographical() {
  const [tab, setTab] = useState(1);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInput, setIsLoadingInput] = useState(false);
  const [editMode, setEditMode] = useState(false); // To track edit mode
  const [currentData, setCurrentData] = useState({});
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Separate states for each dropdown field
  const [type, setType] = useState("region"); // Selected type from dropdown
  const [submittedType, setSubmittedType] = useState("region");
  const [region, setRegion] = useState(null);
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [district, setDistrict] = useState(null);
  const [city, setCity] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState(2);
  const [active, setActive] = useState(1);
  const [searchClicked, setSearchClicked] = useState(false);
  const [geoData, setGeoData] = useState([]);

  // Options for dropdowns
  const [regionOptions, setRegionOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);

  const userCompany = useUserCompany();
  const [selectedValues, setSelectedValues] = useState({});

  const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null
  );

  useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
  }, [userCompany]);
  console.log("User Company:", userCompany);

  const initParamState = (geoHierarchy, countryId = null) => {
    const state = { param1: countryId };

    geoHierarchy.forEach((geoUnit, idx) => {
      state[`param${geoUnit.gldSeq + 1}`] = null;
    });

    return state;
  };

  const geoHierarchy = Object.values(
    userCompany.geoTemplate.geoTemplateHierarchy
  ).sort((a, b) => a.gldSeq - b.gldSeq);

  console.log("selected type", selectedFormType);
  const [paramState, setParamState] = useState(() =>
    initParamState(geoHierarchy)
  );

  const handleResultSelect = (seq, data) => {
    setSelectedValues((prev) => ({ ...prev, [seq]: data }));

    setParamState((prev) => {
      const newParams = { ...prev };
      newParams[`param${seq + 1}`] = data;

      const maxSeq = Object.keys(newParams).length;
      for (let i = seq + 2; i <= maxSeq; i++) {
        newParams[`param${i}`] = null;
      }
      return newParams;
    });
  };

  const handleClear = (seq) => {
    setParamState((prev) => {
      const newState = { ...prev };
      const keys = Object.keys(newState);
      const startIndex = seq + 1;

      keys.forEach((key, index) => {
        const paramIndex = index + 1;
        if (paramIndex >= startIndex) {
          newState[key] = null;
        }
      });

      return newState;
    });

    setSelectedValues((prev) => ({
      ...prev,
      [seq]: null,
    }));
  };

  console.log("Param State:", paramState);
  console.log("country", country);

  const typeOptions = useMemo(() => {
    if (!userCompany?.geoTemplate?.geoTemplateHierarchy) return [];

    const fixedOptions = [
      { key: -2, value: -2, text: "Region" },
      { key: -1, value: -1, text: "Country" },
    ];

    const dynamicOptions = geoHierarchy.map((item) => ({
      key: item.gldSeq,
      value: item.gldSeq,
      text: item.geoUnitValue,
    }));

    return [...fixedOptions, ...dynamicOptions];
  }, [userCompany]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoadingInput(true);

        const [regions, statuses, actives] = await Promise.all([
          regionDropdown(),
          getDropdownData("status"),
          getDropdownData("active"),
        ]);

        setRegionOptions(regions);
        setStatusOptions(statuses);
        setActiveOptions(actives);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      } finally {
        setIsLoadingInput(false);
      }
    })();
  }, [country, state, district]);

  useEffect(() => {
    console.log("State updated: ", {
      region,
      country,
      state,
      district,
      city,
      searchFieldReset,
    });
  }, [region, country, state, district, city, searchFieldReset]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (type !== -2 && type !== -1 && !paramState.param1) {
      toast.error("Please Select Type", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    setIsLoading(true);
    setSubmittedType(type);

    const payload = {
      type,
      region,
      active,
      ...paramState,
      template_id: userCompany.geoTemplate.gldTemplateId,
    };

    console.log("Search Payload:", payload);
    console.log("Final Payload:", JSON.stringify(payload, null, 2));

    try {
      const data = await geoSearch(payload);
      setGeoData(data);
      setSearchClicked(true);
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleAccordionClick = (index) => {
    setActiveAccordion(index === activeAccordion ? null : index);
  };

  const getGldIdByType = (type) => {
    if (!userCompany?.geoTemplate?.geoTemplateHierarchy) return null;

    const geoHierarchy = Object.values(
      userCompany.geoTemplate.geoTemplateHierarchy
    );
    const match = geoHierarchy.find((item) => item.gldSeq === type);
    return match ? match.gldId : null;
  };

  const handleRowDoubleClick = async (rowData) => {
    console.log(rowData);
    try {
      setEditMode(true);
      let typeForUpdate;
      let gldId = null;

      if (submittedType === -2) {
        typeForUpdate = -2;
      } else if (submittedType === -1) {
        typeForUpdate = -1;
      } else {
        const geoUnit = geoHierarchy.find(
          (item) => item.gldSeq == submittedType
        );
        if (geoUnit) {
          gldId = geoUnit.gldId;
          typeForUpdate = gldId;
        }
      }
      let fetchedData = rowData.data;
      if (gldId) {
        try {
          const response = await getGeoUnitById(gldId, rowData.data.gdId);
          fetchedData = response;
        } catch (e) {
          console.error("Error fetching details:", e);
        }
      } else if (submittedType === -2) {
        fetchedData = await getByIdRegion(rowData.data.gdId);
      } else if (submittedType === -1) {
        fetchedData = await countryDetailsById(rowData.data.gdId);
      }
      console.log("gld id-->", selectedFormType);

      setCurrentData(fetchedData);
      setSelectedFormType(typeForUpdate);
      setDrawerOpen(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const actionMenuOptions = useMemo(() => {
    if (!userCompany?.geoTemplate?.geoTemplateHierarchy) return [];
    return Object.values(userCompany.geoTemplate.geoTemplateHierarchy)
      .sort((a, b) => a.gldSeq - b.gldSeq)
      .map((item) => ({
        key: item.gldId,
        value: item.geoUnitValue.toLowerCase(),
        text: `Add ${item.geoUnitValue}`,
        tab: item.gldSeq + 1,
      }));
  }, [userCompany]);

  const handleActionMenuSelect = (selectedType) => {
    setEditMode(false);
    setCurrentData({});
    setSelectedFormType(selectedType);
    const selectedOption = actionMenuOptions.find(
      (opt) => opt.value === selectedType
    );
    setTab(selectedOption ? selectedOption.tab : 1);

    setDrawerOpen(true);
  };

  const getDynamicColumnConfig = (unitValue) => {
    return [
      { headerName: `${unitValue} Code`, field: "gdCode" },
      { headerName: `${unitValue} Name`, field: "gdName" },

      {
        headerName: "Active",
        field: "gdActiveName",
        cellRenderer: ActiveCellStyle,
      },
    ];
  };

  const getColumnConfig = () => {
    switch (submittedType) {
      case -2:
        return [
          { headerName: "Region Code", field: "gdCode" },
          { headerName: "Region Name", field: "gdName" },
          {
            headerName: "Active",
            field: "gdActiveName",
            cellRenderer: ActiveCellStyle,
          },
        ];
      case -1:
        return [
          { headerName: "Country Code", field: "gdCode" },
          { headerName: "Country Name", field: "gdName" },

          {
            headerName: "Active",
            field: "gdActiveName",
            cellRenderer: ActiveCellStyle,
          },
        ];

      default:
        if (!userCompany?.geoTemplate?.geoTemplateHierarchy) return [];

        const columnConfig = geoHierarchy.find(
          (item) => item.gldSeq === submittedType
        );
        return columnConfig
          ? getDynamicColumnConfig(columnConfig.geoUnitValue)
          : [];
    }
  };

  if (!userCompany?.geoTemplate?.geoTemplateHierarchy) {
    return null;
  }

  return (
    <>
      <div
        className="ui attached tabular"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div className="ui top attached tabular menu">
          <Tab
            Icon={Info}
            onClick={() => setTab(1)}
            active={tab === 1}
            label="Geographical"
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <ActionMenu
            title="Add"
            options={actionMenuOptions}
            onChangeHandler={handleActionMenuSelect}
          />
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion === 1 ? "active" : ""}`}
            onClick={() => handleAccordionClick(1)}
          >
            <div>
              Search
              <Search size={14} style={{ marginLeft: "10px" }} />
              <i className="dropdown icon"></i>
            </div>
          </div>
          <div className={`content ${activeAccordion === 1 ? "active" : ""}`}>
            <div className="ui form">
              <h4 className="ui dividing header">Geographical Information</h4>
              <div className="field">
                <div className="five fields">
                  <div className="four wide field required">
                    <label>Type</label>
                    <Select
                      placeholder="Select Type"
                      options={typeOptions}
                      value={type}
                      style={{ height: "5px" }}
                      onChange={(e, data) => setType(data.value)}
                    />
                  </div>
                  <div className="four wide field">
                    <label>
                      Region{" "}
                      {type > 0 && <span style={{ color: "red" }}>*</span>}
                    </label>
                    <Select
                      placeholder="Select Region"
                      options={regionOptions}
                      value={region}
                      search={true}
                      style={{ height: "5px" }}
                      onChange={(e, data) => setRegion(data.value)}
                    />
                  </div>
                  <div className="four wide field">
                    <SearchDropdown
                      label={
                        <>
                          Country{" "}
                          {type !== -2 && type !== -1 && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </>
                      }
                      placeholder="Select Country"
                      fieldInfo={{
                        tableName: "masterdata.tms_m_reg_country",
                        primaryKey: "cou_id",
                        fieldName: "cou_country",
                        id: country,
                      }}
                      api={countryNameByRegion}
                      apiDependency={{
                        id: region,
                      }}
                      value={country}
                      clearable={true}
                      onClear={() => {
                        setCountry(null);
                        setParamState(() => ({
                          param1: null,
                        }));
                      }}
                      onResultSelect={(data) => {
                        setCountry(data);
                        setParamState((prevState) => ({
                          ...prevState,
                          param1: data,
                        }));
                      }}
                      reset={searchFieldReset}
                      setReset={setSearchFieldReset}
                      disabled={type === -2}
                    />
                  </div>
                  {/* <div className="four wide field">
                        <SearchDropdown
                          label={
                            <>
                              State{" "}
                              {["district", "city"].includes(type) && (
                                <span style={{ color: "red" }}>*</span>
                              )}
                            </>
                          }
                          placeholder="Select State"
                          fieldInfo={{
                            tableName: "masterdata.tms_m_reg_state",
                            primaryKey: "stt_id",
                            fieldName: "stt_state_name",
                            id: state,
                          }}
                          api={stateByCountryAndStartsWith}
                          apiDependency={{
                            id: country,
                          }}
                          value={state}
                          reset={searchFieldReset}
                          clearable={true}
                          onClear={() => {
                            setState(null);
                          }}
                          onResultSelect={(data) => setState(data)}
                          setReset={setSearchFieldReset}
                          disabled={type === "region" || type === "country"}
                        />
                      </div> */}
                  {/* 
                        <div className="four wide field">
                          <SearchDropdown
                            label={
                              <>
                                District{" "}
                                {["city"].includes(type) && (
                                  <span style={{ color: "red" }}>*</span>
                                )}
                              </>
                            }
                            placeholder="Select District"
                            fieldInfo={{
                              tableName: "masterdata.tms_m_reg_district",
                              primaryKey: "dis_id",
                              fieldName: "dis_district",
                              id: district,
                            }}
                            api={districtByStateAndStartsWith}
                            apiDependency={{ id: state }}
                            value={district}
                            clearable={true}
                            onResultSelect={(data) => setDistrict(data)}
                            setReset={setSearchFieldReset}
                            reset={searchFieldReset}
                            disabled={
                              type === "region" ||
                              type === "country" ||
                              type === "state"
                            }
                          />
                        </div>

                        <div className="four wide field">
                          <SearchDropdown
                            placeholder="Select City"
                            fieldInfo={{
                              tableName: "masterdata.tms_m_reg_cities",
                              primaryKey: "cit_id",
                              fieldName: "cit_city",
                              id: city,
                            }}
                            api={cityByStateAndStartsWith}
                            apiDependency={{ id: state }}
                            value={city}
                            label="City"
                            clearable={true}
                            onResultSelect={(data) => setCity(data)}
                            setReset={setSearchFieldReset}
                            reset={searchFieldReset}
                            disabled={
                              type === "region" ||
                              type === "country" ||
                              type === "state" ||
                              type === "district"
                            }
                          />
                        </div> */}

                  {geoHierarchy.map((geoUnit, index) => {
                    const targetParam = `param${index + 1}`;
                    console.log("type", type, geoUnit.gldSeq);
                    return (
                      <div key={geoUnit.gldSeq} className="four wide field">
                        <SearchDropdown
                          label={<>{geoUnit.geoUnitValue}</>}
                          placeholder={`Select ${geoUnit.geoUnitValue}`}
                          fieldInfo={{
                            id: selectedValues[geoUnit.gldSeq],
                          }}
                          api={geoDropdownById}
                          apiDependency={{
                            id: geoUnit.gldId,
                            params: paramState,
                          }}
                          value={selectedValues[geoUnit.gldSeq] || null}
                          clearable={true}
                          onClear={() => handleClear(geoUnit.gldSeq)}
                          reset={searchFieldReset}
                          setReset={setSearchFieldReset}
                          onResultSelect={(data) =>
                            handleResultSelect(geoUnit.gldSeq, data)
                          }
                          disabled={!country || type < geoUnit.gldSeq}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="field">
                <div className="six fields">
                  <Dropdown
                    label="Active"
                    placeholder="Select Active"
                    options={activeOptions}
                    value={active}
                    onChange={(e, data) => setActive(data.value)}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                <Button
                  template="searchReset"
                  onClick={() => {
                    // Reset dependent states in Geographical
                    setType("region");
                    setRegion(null);
                    setCountry(null);
                    setState(null);
                    setDistrict(null);
                    setCity(null);
                    setFromDate(null);
                    setToDate(null);
                    setStatus(2);
                    setActive(1);

                    // Force the SearchDropdown components to reset
                    setSearchFieldReset(true); // Set reset to true
                    setTimeout(() => setSearchFieldReset(false), 100); // Add a small delay before toggling back

                    console.log(
                      "Type: ",
                      type,
                      region,
                      country,
                      state,
                      district,
                      city,
                      searchFieldReset
                    );
                  }}
                />

                <Button
                  template="search"
                  type="submit"
                  onClick={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          searchClicked && (
            <Grid
              content={geoData}
              config={getColumnConfig()}
              onRowDoubleClicked={handleRowDoubleClick}
            />
          )
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        body={
          editMode ? (
            <GeoUnitsUpdateComponent
              onClose={handleDrawerClose}
              selectedType={selectedFormType}
              isOpen={isDrawerOpen}
              currentData={currentData}
            />
          ) : (
            <GeoManagerAddComponent
              onClose={handleDrawerClose}
              isOpen={isDrawerOpen}
              editMode={editMode} // Pass editMode to GeographicalAddComponent
              currentData={currentData} // Pass currentData to GeographicalAddComponent
              selectedType={selectedFormType} // Pass selectedFormType as selectedType
            />
          )
        }
        style={{ width: "60vw" }}
      />
    </>
  );
}

export default Geographical;
